import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import PDFViewer from '@/components/PDFViewer'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import {
    acceptRegulations,
    getActiveRegulationByToken,
    getRegulationFileUrlByToken,
    getMyRegulationStatusByToken,
} from '@/modules/Regulations/services'
import { RegulationsConfig } from '@/modules/Regulations/types'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import React, {
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Button, Checkbox, Typogaphy } from 'vnetwork-ui'

interface RegulationsGateProps {
    token: string
    onAccepted: () => void
}

const RegulationsGate: React.FC<RegulationsGateProps> = ({
    token,
    onAccepted,
}) => {
    const { t } = useTranslation()

    const [hasViewedAllPages, setHasViewedAllPages] = useState(false)
    const [hasAgreed, setHasAgreed] = useState(false)
    // Mobile: summary HIỆN mặc định (như bình thường), bấm ▲ để thu gọn lấy chỗ cho PDF
    // (desktop luôn hiện đầy đủ, không có nút)
    const [summaryExpanded, setSummaryExpanded] = useState(true)

    const {
        data: statusResponse,
        isLoading: isStatusLoading,
        isError: isStatusError,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.GENERIC, 'regulations-my-status', token],
        queryFn: () => getMyRegulationStatusByToken({ token }),
        enabled: !!token,
        retry: false,
    })

    const myStatus = statusResponse?.result
    const needsAcceptance = !!myStatus?.needs_acceptance

    const {
        data: regulationData,
        isLoading: isRegulationLoading,
        isError: isRegulationError,
    } = useQueryWithHandler<RegulationsConfig>({
        queryKey: [QUERY_KEYS.GENERIC, 'regulations-config', token],
        queryFn: () => getActiveRegulationByToken({ token }),
        enabled: !!token && needsAcceptance,
        retry: false,
    })

    useEffect(() => {
        if (!isStatusLoading && myStatus && !myStatus.needs_acceptance) {
            onAccepted()
        }
    }, [isStatusLoading, myStatus?.needs_acceptance, onAccepted])

    const pdfAttachment = useMemo(
        () =>
            regulationData?.attachments?.find(
                (att) => att.attachment_file_type === 'application/pdf'
            ),
        [regulationData?.attachments]
    )
    const pdfPath = pdfAttachment?.attachment_file_url

    const {
        data: presignedUrl,
        isLoading: isFileUrlLoading,
        isError: isFileUrlError,
    } = useQueryWithHandler<string>({
        queryKey: [QUERY_KEYS.GENERIC, 'regulations-file-url', token],
        queryFn: () => getRegulationFileUrlByToken({ token }),
        enabled: !!token && needsAcceptance && !!pdfPath,
        retry: false,
    })

    const handleAllPagesViewed = useCallback(() => {
        setHasViewedAllPages(true)
    }, [])

    const { mutate: submitAcceptance, isPending } = useMutationWithHandler(
        {
            mutationFn: () =>
                acceptRegulations({
                    token,
                    regulation_version:
                        regulationData?.regulation_version || '',
                }),
            onSuccess: () => {
                onAccepted()
            },
        },
        {
            reactQueryKeys: QUERY_KEYS.GENERIC,
            successMessage: 'regulations-accepted-success',
            errorMessage: 'regulations-accepted-error',
        }
    )

    const canSubmit =
        !!regulationData?.regulation_version &&
        hasViewedAllPages &&
        hasAgreed &&
        !isPending

    if (isStatusLoading || isRegulationLoading || isFileUrlLoading) {
        return (
            <div className="fixed inset-0 z-[99999] bg-white flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
            </div>
        )
    }

    // Do not silently bypass regulations check on token flow errors.
    // This keeps compliance strict until backend exposes a public token-based regulations API.
    if (isStatusError || isRegulationError || isFileUrlError) {
        return (
            <div className="fixed inset-0 z-[99999] bg-white flex items-center justify-center px-4">
                <div className="max-w-xl text-center space-y-2">
                    <Typogaphy className="text-red-500 text-lg font-semibold">
                        {t('error-loading-pdf-file')}
                    </Typogaphy>
                    <Typogaphy className="text-sm text-gray-600">
                        Không thể xác thực điều khoản cho link này. Vui lòng
                        liên hệ quản trị viên để được hỗ trợ.
                    </Typogaphy>
                </div>
            </div>
        )
    }

    if (!needsAcceptance) {
        return null
    }

    if (!regulationData) {
        return (
            <div className="fixed inset-0 z-[99999] bg-white flex items-center justify-center px-4">
                <Typogaphy className="text-red-500 text-center">
                    {t('error-loading-pdf-file')}
                </Typogaphy>
            </div>
        )
    }

    if (!pdfPath) {
        return (
            <div className="fixed inset-0 z-[99999] bg-white flex flex-col items-center justify-center px-4">
                <Typogaphy className="text-red-500 text-center">
                    {t('regulations:regulation-file-not-found')}
                </Typogaphy>
            </div>
        )
    }

    return (
        <div className="fixed inset-0 z-[99999] bg-white flex flex-col">
            <nav className="flex-shrink-0 bg-white shadow-sm border-b">
                <div className="px-4 py-3 flex items-center gap-4">
                    <img
                        src="/images/icon.png"
                        alt="VNETWORK"
                        className="h-10 w-auto"
                    />
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <Typogaphy className="text-lg font-semibold text-gray-800 truncate md:overflow-visible md:whitespace-normal">
                                {regulationData?.regulation_title ||
                                    t('regulations:company-regulations-title')}
                            </Typogaphy>
                            {regulationData.regulation_summary && (
                                <button
                                    type="button"
                                    onClick={() =>
                                        setSummaryExpanded((v) => !v)
                                    }
                                    aria-label={t(
                                        'regulations:company-regulations-title'
                                    )}
                                    className="md:hidden flex-shrink-0 text-gray-500 hover:text-gray-700"
                                >
                                    {summaryExpanded ? (
                                        <ChevronUp className="w-5 h-5" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5" />
                                    )}
                                </button>
                            )}
                        </div>
                        {regulationData.regulation_summary && (
                            <p
                                className={`text-sm text-gray-500 ${summaryExpanded ? 'block' : 'hidden'} md:block`}
                            >
                                {regulationData.regulation_summary}
                            </p>
                        )}
                    </div>
                </div>
            </nav>

            <div className="flex-1 overflow-hidden">
                {!presignedUrl && pdfPath ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
                    </div>
                ) : presignedUrl ? (
                    <Suspense
                        fallback={
                            <div className="flex items-center justify-center h-full">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
                            </div>
                        }
                    >
                        <PDFViewer
                            url={presignedUrl}
                            onAllPagesViewed={handleAllPagesViewed}
                            disableDownload
                        />
                    </Suspense>
                ) : (
                    <div className="flex items-center justify-center h-full">
                        <p className="text-red-500">
                            {t('error-loading-pdf-file')}
                        </p>
                    </div>
                )}
            </div>

            <div className="flex-shrink-0 border-t bg-white px-4 py-2 shadow-[0_-2px_4px_rgba(0,0,0,0.05)] max-w-3xl mx-auto">
                <div className="flex items-center justify-between gap-4 w-full">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            checked={hasAgreed}
                            onChange={(e) => setHasAgreed(e.target.checked)}
                            disabled={!hasViewedAllPages}
                        />
                        <label
                            className={`text-sm cursor-pointer select-none ${!hasViewedAllPages ? 'text-gray-400' : 'text-gray-700'}`}
                            onClick={() =>
                                hasViewedAllPages && setHasAgreed(!hasAgreed)
                            }
                        >
                            {t('regulations:regulations-agreement-text')}
                            {!hasViewedAllPages && (
                                <span className="text-xs text-amber-600 ml-1">
                                    (
                                    {t(
                                        'regulations:regulations-scroll-all-pages-hint'
                                    )}
                                    )
                                </span>
                            )}
                        </label>
                    </div>
                    <Button
                        onClick={() => submitAcceptance()}
                        disabled={!canSubmit}
                        loading={isPending}
                        variant="contained"
                        size="small"
                    >
                        {t('regulations:accept-regulations')}
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default RegulationsGate
